import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { v4 as uuidv4 } from 'uuid';
import { chatApi, tracesApi, userAgentApi, ChatThread, ChatStatus } from '@/services/api/agentApi';
import { getAgentSocket } from '@/services/api/agentSocketClient';
import { ensureOptionalAgentServiceAvailable } from '@/services/api/agentServiceAvailability';
import type { Source, TrustMetadata, ReviewStatus } from '@/types/trust';
import { useAuthStore, getValidAccessToken, SessionExpiredError } from '@/stores/auth/auth-store';
import { triggerReAuth } from '@/stores/auth/reauth-store';
import { getCurrentAccessToken } from '@/services/api/auth-session';
import * as errorUtils from '@/utils/errors';
import type {
  IntentDecomposedEvent,
  SubIntentStatusEvent,
  SubIntentResultEvent,
  SynthesisStartEvent,
  IntentKind,
} from '@/types/agent-socket';
import { SYNTHESIZER_INTENT_ID } from '@/types/agent-socket';

export type ChatMode = 'community' | 'user_agent';

export interface ClarificationOption {
  label: string;
  intent: string;
  sources: string[];
}

export interface MessageClarification {
  question: string;
  options: ClarificationOption[];
  topic: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  status?: 'thinking' | 'done' | 'error';
  usage?: { modelName: string; modelTier: number; tokensInput: number; tokensOutput: number };
  onboardingQuestion?: string | null;
  agentName?: string;
  isProgress?: boolean;
  /** Trust-system provenance (Phase 2+). Undefined = not tracked; [] = tracked, none found. */
  sources?: Source[];
  trustMetadata?: TrustMetadata;
  reviewStatus?: ReviewStatus;
  /** Phase C: clarification turn — render with quick-reply chips. */
  clarification?: MessageClarification;
}

export interface LiveIntent {
  id: string;
  kind: IntentKind;
  text: string;
  status: 'pending' | 'done' | 'failed';
  summary?: string;
}

export interface LiveStatusState {
  intents: LiveIntent[];
  currentActivity: string;
  currentIntentId: string | null;
  synthesizing: boolean;
}

interface ChatState {
  // Thread list
  threads: ChatThread[];
  threadsTotal: number;
  isLoadingThreads: boolean;

  // Active conversation
  activeThreadId: string | null;
  messages: ChatMessage[];
  isLoadingMessages: boolean;
  isSending: boolean;

  // Chat mode
  chatMode: ChatMode;

  // Chat status
  chatStatus: ChatStatus | null;

  // Agent identity
  agentName: string | null;

  // Multi-intent live status (Phase 6+) — keyed by messageId
  liveStatus: Record<string, LiveStatusState>;

  // Actions
  fetchThreads: () => Promise<void>;
  loadThread: (threadId: string) => Promise<void>;
  startNewChat: () => void;
  sendMessage: (text: string, communityId?: string) => Promise<void>;
  cancelSend: () => void;
  fetchStatus: () => Promise<void>;
  setChatMode: (mode: ChatMode) => void;
  initUserAgentSocket: () => void;
  fetchAgentName: () => Promise<void>;
  resetOnLogout: () => void;
}

// ---- Socket listener setup (re-bindable across reconnects) ----

let socketListenersInitialized = false;

function normalizeIdentityName(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

function isProbablyCurrentUserName(name: string): boolean {
  const user = useAuthStore.getState().user;
  if (!user) return false;

  const normalized = normalizeIdentityName(name);
  const candidates = [
    `${user.firstName} ${user.lastName}`.trim(),
    user.firstName,
    user.username,
    user.email.split('@')[0],
  ]
    .map((candidate) => normalizeIdentityName(candidate))
    .filter(Boolean);

  return candidates.includes(normalized);
}

function sanitizeAgentName(name?: string | null, namedBy?: 'user' | 'ai'): string | null {
  const trimmed = name?.trim();
  if (!trimmed) return null;
  if (namedBy === 'user' && isProbablyCurrentUserName(trimmed)) {
    return null;
  }
  if (namedBy === undefined && isProbablyCurrentUserName(trimmed)) {
    return null;
  }
  return trimmed;
}

/** Attach all user_agent:* listeners to the current socket. */
function bindSocketListeners(
  socket: ReturnType<typeof getAgentSocket>,
  updateMessage: (messageId: string, updates: Partial<ChatMessage>) => void,
  setActiveThreadId: (threadId: string) => void,
  refreshThreads: () => void,
  setAgentName: (name: string) => void,
  appendMessage: (msg: ChatMessage) => void,
  upsertMessage: (msg: ChatMessage) => void,
) {

  socket.on('user_agent:status', (data: { messageId: string; text: string }) => {
    updateMessage(data.messageId, {
      content: data.text,
      status: 'thinking',
    });
  });

  socket.on(
    'user_agent:response',
    (data: {
      messageId: string;
      threadId?: string;
      response: string;
      usage?: ChatMessage['usage'];
      onboardingQuestion?: string | null;
      warnings?: string[];
      agentName?: string;
      sources?: Source[];
      trustMetadata?: TrustMetadata;
      reviewStatus?: ReviewStatus;
      clarification?: MessageClarification;
    }) => {
      const resolvedAgentName = sanitizeAgentName(data.agentName);
      const warningPrefix = data.warnings?.length
        ? data.warnings.map((w) => `⚠️ ${w}`).join('\n') + '\n\n'
        : '';
      updateMessage(data.messageId, {
        content: warningPrefix + data.response,
        status: 'done',
        usage: data.usage,
        onboardingQuestion: data.onboardingQuestion ?? null,
        agentName: resolvedAgentName ?? undefined,
        sources: data.sources,
        trustMetadata: data.trustMetadata,
        reviewStatus: data.reviewStatus,
        clarification: data.clarification,
      });
      if (resolvedAgentName) {
        setAgentName(resolvedAgentName);
      }
      if (data.threadId) {
        setActiveThreadId(data.threadId);
        refreshThreads();
      }
      useChatStore.setState((s) => {
        delete s.liveStatus[data.messageId];
      });
    },
  );

  socket.on('user_agent:error', (data: { messageId: string; error: string }) => {
    updateMessage(data.messageId, {
      content: `Failed to get a response: ${data.error}`,
      status: 'error',
    });
    useChatStore.setState((s) => {
      delete s.liveStatus[data.messageId];
    });
  });

  socket.on(
    'user_agent:session_expired',
    (data: { messageId?: string; reason?: string }) => {
      const messageId = data.messageId;
      if (messageId) {
        updateMessage(messageId, {
          content: 'Your session has expired. Please sign in again to retry this message.',
          status: 'error',
        });
        useChatStore.setState((s) => {
          delete s.liveStatus[messageId];
        });
      }
      useChatStore.setState((s) => {
        s.isSending = false;
      });
      triggerReAuth('session_expired');
    },
  );

  // ---- Multi-intent live status (Phase 6+) ----
  const subStatusThrottle = new Map<string, ReturnType<typeof setTimeout>>();

  socket.on('user_agent:intent_decomposed', (data: IntentDecomposedEvent) => {
    useChatStore.setState((s) => {
      s.liveStatus[data.messageId] = {
        intents: data.intents.map((i) => ({
          id: i.id,
          kind: i.kind,
          text: i.text,
          status: 'pending' as const,
        })),
        currentActivity: '',
        currentIntentId: null,
        synthesizing: false,
      };
    });
  });

  socket.on('user_agent:sub_status', (data: SubIntentStatusEvent) => {
    const pending = subStatusThrottle.get(data.messageId);
    if (pending) clearTimeout(pending);
    subStatusThrottle.set(
      data.messageId,
      setTimeout(() => {
        subStatusThrottle.delete(data.messageId);
        useChatStore.setState((s) => {
          if (!s.liveStatus[data.messageId]) {
            s.liveStatus[data.messageId] = {
              intents: [],
              currentActivity: '',
              currentIntentId: null,
              synthesizing: false,
            };
          }
          const status = s.liveStatus[data.messageId];
          status.currentActivity = data.thought;
          status.currentIntentId = data.intentId;
          if (data.intentId === SYNTHESIZER_INTENT_ID) {
            status.synthesizing = true;
          }
        });
      }, 200),
    );
  });

  socket.on('user_agent:sub_result', (data: SubIntentResultEvent) => {
    useChatStore.setState((s) => {
      const status = s.liveStatus[data.messageId];
      if (!status) return;
      const intent = status.intents.find((i) => i.id === data.intentId);
      if (intent) {
        intent.status = data.status === 'ok' ? 'done' : 'failed';
        intent.summary = data.summary;
      }
    });
  });

  socket.on('user_agent:synthesis_start', (data: SynthesisStartEvent) => {
    useChatStore.setState((s) => {
      const status = s.liveStatus[data.messageId];
      if (status) status.synthesizing = true;
    });
  });

  // Async task results — delivered when a background domain agent query completes
  socket.on(
    'user_agent:task_result',
    (data: {
      taskId: string;
      messageId: string;
      response?: string;
      error?: string;
      usage?: ChatMessage['usage'];
      onboardingQuestion?: string | null;
      warnings?: string[];
      agentName?: string;
      sources?: Source[];
      trustMetadata?: TrustMetadata;
      reviewStatus?: ReviewStatus;
      clarification?: MessageClarification;
    }) => {
      const resolvedAgentName = sanitizeAgentName(data.agentName);
      if (data.error) {
        appendMessage({
          id: `task-error-${data.taskId}`,
          role: 'assistant',
          content: `I wasn't able to get that information: ${data.error}`,
          timestamp: new Date(),
          status: 'error',
        });
        return;
      }

      const warningPrefix = data.warnings?.length
        ? data.warnings.map((w) => `⚠️ ${w}`).join('\n') + '\n\n'
        : '';

      // Upsert: replace the original thinking bubble (id=messageId) with the
      // final answer if it exists, otherwise append a fresh bubble. Without
      // this, async deliveries leave the user staring at stale thinking dots
      // while the actual response shows up as a separate bubble below.
      upsertMessage({
        id: data.messageId || `task-result-${data.taskId}`,
        role: 'assistant',
        content: warningPrefix + (data.response || ''),
        timestamp: new Date(),
        status: 'done',
        usage: data.usage,
        onboardingQuestion: data.onboardingQuestion ?? null,
        agentName: resolvedAgentName ?? undefined,
        sources: data.sources,
        trustMetadata: data.trustMetadata,
        reviewStatus: data.reviewStatus,
        clarification: data.clarification,
      });

      if (resolvedAgentName) {
        setAgentName(resolvedAgentName);
      }
    },
  );

  // Research progress updates — lightweight status messages during long-running research
  socket.on(
    'user_agent:task_progress',
    (data: {
      taskId: string;
      sessionId: string;
      summary: string;
      iteration?: number;
      findingsCount?: number;
      pagesScraped?: number;
      totalCostUsd?: number;
      budgetUsd?: number;
    }) => {
      appendMessage({
        id: `task-progress-${data.taskId}-${data.iteration || Date.now()}`,
        role: 'assistant',
        content: data.summary,
        timestamp: new Date(),
        status: 'done',
        isProgress: true,
      });
    },
  );
}

type SocketListenerCallbacks = [
  updateMessage: (messageId: string, updates: Partial<ChatMessage>) => void,
  setActiveThreadId: (threadId: string) => void,
  refreshThreads: () => void,
  setAgentName: (name: string) => void,
  appendMessage: (msg: ChatMessage) => void,
  upsertMessage: (msg: ChatMessage) => void,
];

// Store callbacks so we can re-bind after socket reconnect
let savedCallbacks: SocketListenerCallbacks | undefined;

function setupSocketListeners(
  updateMessage: (messageId: string, updates: Partial<ChatMessage>) => void,
  setActiveThreadId: (threadId: string) => void,
  refreshThreads: () => void,
  setAgentName: (name: string) => void,
  appendMessage: (msg: ChatMessage) => void,
  upsertMessage: (msg: ChatMessage) => void,
) {
  savedCallbacks = [updateMessage, setActiveThreadId, refreshThreads, setAgentName, appendMessage, upsertMessage];

  if (socketListenersInitialized) return;
  socketListenersInitialized = true;

  const socket = getAgentSocket();
  bindSocketListeners(socket, ...savedCallbacks);
}

/**
 * Re-attach listeners after an agent socket reconnect (e.g. after re-auth).
 * Called from reconnectAgentSocket via an event or directly.
 */
export function rebindAgentSocketListeners(): void {
  if (!savedCallbacks) return;
  const socket = getAgentSocket();
  bindSocketListeners(socket, ...savedCallbacks);
}

export const useChatStore = create<ChatState>()(
  immer((set, get) => ({
    threads: [],
    threadsTotal: 0,
    isLoadingThreads: false,
    activeThreadId: null,
    messages: [],
    isLoadingMessages: false,
    isSending: false,
    chatMode: 'community',
    chatStatus: null,
    agentName: null,
    liveStatus: {},

    initUserAgentSocket: () => {
      // Set up persistent socket listeners that update the store
      setupSocketListeners(
        (messageId, updates) => {
          set((s) => {
            const idx = s.messages.findIndex((m) => m.id === messageId);
            if (idx !== -1) {
              Object.assign(s.messages[idx], updates, { timestamp: new Date() });
              // Mark isSending false when response or error arrives
              if (updates.status === 'done' || updates.status === 'error') {
                s.isSending = false;
              }
            }
          });
        },
        (threadId) => {
          set((s) => { s.activeThreadId = threadId; });
        },
        () => { get().fetchThreads(); },
        (name) => {
          set((s) => { s.agentName = name; });
        },
        (msg) => {
          set((s) => { s.messages.push(msg); });
        },
        // upsertMessage: replace existing message by id, else append.
        // Used by async task_result so the original thinking bubble is
        // updated in place instead of leaving stale dots on screen.
        (msg) => {
          set((s) => {
            const idx = s.messages.findIndex((m) => m.id === msg.id);
            if (idx !== -1) {
              Object.assign(s.messages[idx], msg, { timestamp: new Date() });
            } else {
              s.messages.push(msg);
            }
            if (msg.status === 'done' || msg.status === 'error') {
              s.isSending = false;
            }
          });
        },
      );
    },

    fetchThreads: async () => {
      const { chatMode } = get();
      set((s) => { s.isLoadingThreads = true; });
      try {
        if (!(await ensureOptionalAgentServiceAvailable())) {
          set((s) => { s.isLoadingThreads = false; });
          return;
        }

        if (chatMode === 'user_agent') {
          const res = await tracesApi.listThreads({ agentRoute: 'user-agent', limit: 30 });
          const threads: ChatThread[] = res.data.threads.map((t) => ({
            thread_id: t.thread_id,
            title: t.title,
            status: t.status,
            turn_count: t.turn_count,
            created_at: t.created_at,
            updated_at: t.updated_at,
          }));
          set((s) => {
            s.threads = threads;
            s.threadsTotal = res.data.total;
            s.isLoadingThreads = false;
          });
        } else {
          const res = await chatApi.listThreads(30);
          set((s) => {
            s.threads = res.data.threads;
            s.threadsTotal = res.data.total;
            s.isLoadingThreads = false;
          });
        }
      } catch {
        set((s) => { s.isLoadingThreads = false; });
      }
    },

    loadThread: async (threadId: string) => {
      const { chatMode } = get();
      set((s) => {
        s.activeThreadId = threadId;
        s.isLoadingMessages = true;
        s.messages = [];
      });
      try {
        if (chatMode === 'user_agent') {
          // Reconstruct conversation from tracing spans
          const spansRes = await tracesApi.getSpans(threadId);
          const messages: ChatMessage[] = [];
          const sorted = [...spansRes.data].sort((a, b) =>
            a.turn_index !== b.turn_index ? a.turn_index - b.turn_index : (a.kind === 'user_message' ? -1 : 1)
          );
          for (const span of sorted) {
            const content = span.detail?.content as string | undefined;
            if (!content) continue;
            if (span.kind === 'user_message') {
              messages.push({ id: span.span_id, role: 'user', content, timestamp: new Date(), status: 'done' });
            } else if (span.kind === 'final_response') {
              messages.push({ id: span.span_id, role: 'assistant', content, timestamp: new Date(), status: 'done' });
            }
          }
          set((s) => { s.messages = messages; s.isLoadingMessages = false; });
        } else {
          const res = await chatApi.getThreadMessages(threadId);
          const messages: ChatMessage[] = res.data.messages.map((m) => ({
            id: m.id,
            role: m.role,
            content: m.content,
            timestamp: new Date(m.timestamp),
            usage: m.usage,
            status: 'done' as const,
          }));
          set((s) => { s.messages = messages; s.isLoadingMessages = false; });
        }
      } catch {
        set((s) => { s.isLoadingMessages = false; });
      }
    },

    startNewChat: () => {
      set((s) => {
        s.activeThreadId = null;
        s.messages = [];
      });
    },

    sendMessage: async (text: string, communityId?: string) => {
      const { activeThreadId, chatMode, messages } = get();

      const userMsg: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: text,
        timestamp: new Date(),
        status: 'done',
      };

      set((s) => {
        s.messages.push(userMsg);
        // isSending only for community (HTTP) mode — user_agent is fire-and-forget
        if (chatMode !== 'user_agent') s.isSending = true;
      });

      if (chatMode === 'user_agent') {
        // Use Socket.IO — fire and forget, response comes via socket events
        const messageId = uuidv4();
        const chatHistory = messages.slice(-6).map((m) => ({ role: m.role, content: m.content }));

        // Add "thinking" placeholder that socket events will update
        const thinkingMsg: ChatMessage = {
          id: messageId,
          role: 'assistant',
          content: 'Thinking...',
          timestamp: new Date(),
          status: 'thinking',
        };

        set((s) => {
          s.messages.push(thinkingMsg);
        });

        try {
          const authToken = await getValidAccessToken();
          const socket = getAgentSocket({ connect: true });
          socket.emit('user_agent:message', { message: text, messageId, chatHistory, authToken, thread_id: activeThreadId || undefined });
        } catch (err: unknown) {
          const sessionExpired = err instanceof SessionExpiredError;
          set((s) => {
            const idx = s.messages.findIndex((m) => m.id === messageId);
            if (idx !== -1) {
              s.messages[idx].content = sessionExpired
                ? 'Your session has expired. Please sign in again to send this message.'
                : errorUtils.getErrorMessageOrFallback(err, 'Failed to connect to agent service.');
              s.messages[idx].status = 'error';
            }
            s.isSending = false;
          });
          if (sessionExpired) {
            triggerReAuth('session_expired');
          }
        }
        // isSending stays true until the socket 'user_agent:response' or 'user_agent:error' arrives
        return;
      }

      // Community chat via LiaisonAgent (unchanged)
      try {
        const res = await chatApi.send(text, communityId, activeThreadId || undefined);
        const warnings = res.data.warnings;
        const warningPrefix = warnings?.length
          ? warnings.map((w) => `⚠️ ${w}`).join('\n') + '\n\n'
          : '';
        const assistantMsg: ChatMessage = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: warningPrefix + res.data.response,
          timestamp: new Date(),
          status: 'done',
          usage: res.data.usage,
          onboardingQuestion: res.data.userAgentQuestion || null,
        };
        set((s) => {
          s.activeThreadId = res.data.threadId;
          s.messages.push(assistantMsg);
          s.isSending = false;
        });
        get().fetchThreads();
      } catch (error: unknown) {
        const errorMsg: ChatMessage = {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: errorUtils.getApiErrorMessage(error, 'Failed to get a response. Please try again.'),
          timestamp: new Date(),
          status: 'error',
        };
        set((s) => {
          s.messages.push(errorMsg);
          s.isSending = false;
        });
      }
    },

    cancelSend: () => {
      console.log('[ChatStore] cancelSend called, current isSending:', get().isSending);
      set((s) => {
        s.isSending = false;
        let thinkingIdx = -1;
        for (let i = s.messages.length - 1; i >= 0; i--) {
          if (s.messages[i].status === 'thinking') { thinkingIdx = i; break; }
        }
        console.log('[ChatStore] thinkingIdx:', thinkingIdx);
        if (thinkingIdx !== -1) {
          s.messages[thinkingIdx].content = 'Cancelled.';
          s.messages[thinkingIdx].status = 'done';
        }
      });
    },

    fetchStatus: async () => {
      try {
        if (!(await ensureOptionalAgentServiceAvailable())) {
          set((s) => { s.chatStatus = { available: false, orchestratorCount: 0, orchestrators: [] }; });
          return;
        }

        const res = await chatApi.getStatus();
        set((s) => { s.chatStatus = res.data; });
      } catch {
        set((s) => { s.chatStatus = { available: false, orchestratorCount: 0, orchestrators: [] }; });
      }
    },

    fetchAgentName: async () => {
      try {
        if (!(await ensureOptionalAgentServiceAvailable())) {
          return;
        }

        const res = await userAgentApi.getAgentIdentity();
        const resolvedAgentName = sanitizeAgentName(res.data?.name, res.data?.named_by);
        set((s) => { s.agentName = resolvedAgentName; });
      } catch {
        // Non-critical — agent name is cosmetic
      }
    },

    resetOnLogout: () => {
      socketListenersInitialized = false;
      savedCallbacks = undefined;
      set((s) => {
        s.threads = [];
        s.threadsTotal = 0;
        s.activeThreadId = null;
        s.messages = [];
        s.isLoadingMessages = false;
        s.isLoadingThreads = false;
        s.isSending = false;
        s.chatStatus = null;
        s.agentName = null;
        s.chatMode = 'community';
        s.liveStatus = {};
      });
    },

    setChatMode: (mode: ChatMode) => {
      set((s) => {
        s.chatMode = mode;
        s.activeThreadId = null;
        s.messages = [];
        s.threads = [];
      });
      get().fetchThreads();
    },
  }))
);
